import React, { FunctionComponent, ReactNode, useState } from 'react'
import Grid from '@mui/material/Grid'
import styled from 'styled-components'
import { ReactComponent as Coins } from '../../../assets/images/coins.svg'
import { colors, fontSize, spacing } from '../../../styles/_var'
import { ReactComponent as Trophy } from '../../../assets/images/trophy.svg'
import Text from '../../../components/Text'
import Link from '../../../components/Link'
import { routes } from '../../../utils/constants'
import { AccountIcon, CloseIcon, PrivateIcon } from '../../../components/Icons'

interface IAction {
  actionText: string,
  actionLink: string,
}

interface IStateItem {
  bgColor: string,
  title: string,
  HeaderIcon: ReactNode,
  body: string,
  actions: IAction[]
}

const DashboardSections: IStateItem[] = [
  {
    title: "Campaigns",
    HeaderIcon: <Trophy />,
    body: "Hello and welcome to Matuskii. If you would like to begin creating your own competition or campaign please click on the button below.",
    bgColor: colors.subtlylightpurple,
    actions: [
      {
        actionText: "Create Campaigns",
        actionLink: routes.MY_COMPETITIONS
      }
    ]
  },
  {
    title: "Exlcusive Campaigns",
    HeaderIcon: <PrivateIcon />,
    body: "Have you been invited to participate in any private campaigns? Visit your My Campaigns page and check under the \"Exclusive Campaigns\" Tab",
    bgColor: "#e0ffef",
    actions: [
      {
        actionText: "My Campaigns",
        actionLink: routes.MY_COMPETITIONS
      }
    ]
  },
  {
    title: "Profile Picture",
    HeaderIcon: <AccountIcon />,
    body: "Please update your profile with a recent picture because we would love to put a face to your name.",
    bgColor: colors.yellow,
    actions: [
      {
        actionText: "My Profile",
        actionLink: routes.MY_ACCOUNT
      }
    ]
  },
  {
    title: "Voting Credits",
    HeaderIcon: <Coins />,
    body: "Please purchase voting credits (also called Tush Credits) to participate in campaigns that requires money to vote.",
    bgColor: colors.verylightpurple,
    actions: [
      {
        actionText: "Voting Credits",
        actionLink: routes.VOTING_CREDITS
      }
    ]
  }
]

const DefaultStateItem: FunctionComponent<IStateItem> = ({
  title, HeaderIcon, body, actions, bgColor
}) => {
  const [close, setClose] = useState(false)
  const closeStateItem = () => setClose(true)
  if (close) return null;

  return (
    <Grid md={4} xs={12} sm={6} item>
      <Card>
        <CardHeader bgColor={bgColor}>
          <HeaderLeft>
            {HeaderIcon}
            <Text variant="medium" weight="semi-bold" ml={1}>{title}</Text>
          </HeaderLeft>
          <CloseIcon onClick={closeStateItem}/>
        </CardHeader>
        <CardBody>
          <Text variant='regular'>{body}</Text>
        </CardBody>
        <CardActionArea>
          {actions.map((action, i) => <Link useButton block to={action.actionLink} key={i}>{action.actionText}</Link>)}
        </CardActionArea>
      </Card>
    </Grid>
  )
}

const DashboardDefaultState = () => {
  return (
    <Wrapper>
      <Grid container spacing={5}>
        {DashboardSections.map((section, i) => <DefaultStateItem key={i} {...section} />)}
      </Grid>
    </Wrapper>
  )
}

export default DashboardDefaultState

const Wrapper = styled.div`
  padding: 0 ${spacing.rg} ${spacing.rg};
`

const Card = styled.div`
  color: rgba(0, 0, 0, 0.87);
  transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  border-radius: 4px;
  box-shadow: 0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);
`

const CardHeader = styled.div<{ bgColor: string }>`
  padding: 12px ${spacing.xs};
  display: flex;
  align-items: center;
  border-bottom: solid 1px ${colors.lightgrey};
  justify-content: space-between;
  background-color: ${({ bgColor }) => bgColor};

  svg * {
    fill: ${colors.grey};
    cursor: pointer;
  }

  svg {
    transition: transform ease .2s;
  }

  svg:hover {
    transform: scale(1.3);
  }
`

const CardBody = styled.div`
  padding: 15px ${spacing.xs};
  border-bottom: solid 1px ${colors.lightgrey};
`

const CardActionArea = styled.div`
  padding: ${spacing.xs} ${spacing.xs};
  a {
    font-size: ${fontSize.rg};
    text-decoration: none;
    font-weight: bold;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`